import React, { useMemo, useState } from "react";
import {
  Container,
  Alert,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Pagination,
  Stack,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  LinearProgress,
} from "@mui/material";
import {
  Phone,
  Mic,
  Clock,
  UserRound,
  CheckCircle2,
  XCircle,
  Send,
  Ban,
  MailCheck,
  RefreshCcw,
} from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import { getErrorResponse } from "../../utils/util";

const STATUS_OPTIONS = ["read", "failed", "delivered", "sent", "cancelled"];

const STATUS_META = {
  read: { color: "success", icon: CheckCircle2 },
  delivered: { color: "info", icon: MailCheck },
  sent: { color: "primary", icon: Send },
  failed: { color: "error", icon: XCircle },
  cancelled: { color: "default", icon: Ban },
};

// Body text is unstructured, so pull the fields out with a small parser
const parseBody = (body = "") => {
  const get = (label) => {
    const re = new RegExp(`${label}:\\s*(.*)`);
    const match = body.match(re);
    return match ? match[1].trim() : "";
  };
  return {
    customerNumber: get("Customer Number"),
    voiceClip: get("Recorded Voice Clip"),
    dateTime: get("Date & Time"),
    duration: get("Duration"),
    agentNumber: get("Agent Number"),
  };
};

const AllAgentNotifications = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [customerId, setCustomerId] = useState("");

  const { data: cusIdsRes } = useApiEp(`/api/customer-ids`);
  const cusIds = Array.isArray(cusIdsRes) ? cusIdsRes : cusIdsRes?.data || [];

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("limit", 10);
    if (status) params.set("status", status);
    if (customerId) params.set("customerId", customerId);
    return params.toString();
  }, [page, status, customerId]);

  const { data, isLoading, isError, error } = useApiEp(
    `/root/get-all-agent-notifications?${queryString}`
  );

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const handleReset = () => {
    setStatus("");
    setCustomerId("");
    setPage(1);
  };

  const list = data?.data || [];

  return (
    <Container sx={{ mt: 3, mb: 5 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Agent Notifications
      </Typography>

      {/* Filters Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 3 }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={status}
            onChange={handleStatusChange}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          options={cusIds}
          value={customerId || null}
          onChange={(event, newValue) => {
            setCustomerId(newValue || "");
            setPage(1);
          }}
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Customer" />}
        />

        <Tooltip title="Reset filters">
          <IconButton onClick={handleReset} color="primary">
            <RefreshCcw size={20} />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        <Typography variant="body2" color="text.secondary">
          {data?.totalItems ?? 0} results
        </Typography>
      </Stack>

      {/* Error handling displayed inline rather than unmounting the page */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorResponse(error)}
        </Alert>
      )}

      {/* Horizontal Progress Bar - 4px fixed height prevents UI jumping when it appears/disappears */}
      <Box sx={{ width: "100%", height: 4, mb: 1 }}>
        {isLoading && <LinearProgress />}
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer ID</TableCell>
              <TableCell>Customer Number</TableCell>
              <TableCell>Agent Number</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Voice Clip</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Make sure we don't show "No notifications found" while it is actively loading */}
            {list.length === 0 && !isLoading && !isError && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No notifications found.
                </TableCell>
              </TableRow>
            )}

            {list.map((item) => {
              const parsed = parseBody(item.body);
              const meta = STATUS_META[item.status] || {
                color: "default",
                icon: CheckCircle2,
              };
              const StatusIcon = meta.icon;
              const hasClip =
                parsed.voiceClip && parsed.voiceClip.toUpperCase() !== "NA";

              return (
                <TableRow key={item._id} hover>
                  <TableCell>{item.cusId}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Phone size={14} />
                      <span>{parsed.customerNumber || "-"}</span>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <UserRound size={14} />
                      <span>{parsed.agentNumber || "-"}</span>
                    </Stack>
                  </TableCell>
                  <TableCell>{parsed.dateTime || "-"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Clock size={14} />
                      <span>{parsed.duration || "-"}</span>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {hasClip ? (
                      <Tooltip title="Play recording">
                        <IconButton
                          size="small"
                          color="primary"
                          component="a"
                          href={parsed.voiceClip}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Mic size={16} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        NA
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={meta.color}
                      icon={<StatusIcon size={14} />}
                      label={item.status}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {data?.totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            shape="rounded"
            disabled={isLoading}
          />
        </Stack>
      )}
    </Container>
  );
};

export default AllAgentNotifications;
